import { useEffect, useState } from 'react';
import { creditBalance, startCheckout } from '../api/billing';
import { PRIVACY_URL, TERMS_URL } from '@/config/legal';
import { Button, Screen, SectionLabel } from '../ui';
import RedeemCode, { type Redeemed } from './account/RedeemCode';

/**
 * The browser buys credits through Stripe Checkout, never RevenueCat.
 *
 * src/payments/webCheckout.ts was written for exactly this client — its header names
 * app.saddle-sites.com — because react-native-purchases is a native module and an IAP
 * button in a browser is a dead end. This also keeps the web builder outside the app's
 * in-app-purchase surface entirely.
 */
export default function Credits() {
  const [credits, setCredits] = useState<number | null>(null);
  const [tier, setTier] = useState<string>('free');
  const known = credits !== null;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    /*
     * Ask what the balance actually IS. A store starting at zero would tell someone they
     * have none before they had spent anything — which is what the phone used to do.
     * A failure leaves it unknown, and unknown shows "—" rather than a wrong number.
     */
    void creditBalance()
      .then((b) => {
        setCredits(b.creditsRemaining);
        setTier(b.tier);
      })
      .catch(() => undefined);
  }, []);

  /** A code's answer carries the new balance, so the number above changes without a reload. */
  function redeemed(result: Redeemed): void {
    if (typeof result.creditsRemaining === 'number') setCredits(result.creditsRemaining);
    if (result.tier) setTier(result.tier);
  }

  async function buy(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const result = await startCheckout();
      if (result.status === 'signin_required') setError('Please sign in before buying credits.');
      else if (result.status === 'error') setError(result.message);
      // 'redirecting' navigates the browser away; nothing left to do.
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen title="Credits">
      <p className="type-display credits-balance">
        {tier === 'unlimited' ? 'Unlimited' : known ? credits : '—'}
      </p>
      <p className="type-body project-meta">
        {tier === 'unlimited'
          ? 'This account has no limit.'
          : 'Credits are spent when Saddle builds or changes an app.'}
      </p>

      {error && <p role="alert" className="type-caption field-error-text">{error}</p>}

      {tier !== 'unlimited' && (
        <Button title="Get more credits" onPress={() => void buy()} loading={busy} />
      )}

      {/* The phone's Account → Credits field. On the web it sits here too, next to the balance
          it changes: the iOS app no longer offers it (App Store 3.1.1), so a code given to
          somebody on an iPhone is redeemed in a browser. */}
      <SectionLabel>Redeem code</SectionLabel>
      <RedeemCode tier={known ? tier : null} onRedeemed={redeemed} />

      <p className="type-caption legal-links">
        <a href={TERMS_URL} target="_blank" rel="noreferrer">Terms</a>
        {' · '}
        <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Privacy Policy</a>
      </p>
    </Screen>
  );
}
